import { useState } from 'react';
import moment from 'moment-timezone';
import { formatCurrencyInput } from 'utility/numbers';

/**
 * Manage text state for TextInput | TextArea components
 * @param {Object} options
 * @param {string} [options.defaultValue]
 * @param {boolean} [options.disabled=false]
 * @param {boolean} [options.noSpaces]
 * @param {'alphanumeric'|'numeric'|'urlSafe'} [options.format]
 * @param {number} [options.maxLength]
 * @returns {{value: string, setValue: (value: string) => void, setError: (message: string) => void, setDisabled: (disabled: boolean) => void, formProps: {value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, error: boolean, helperText: string, disabled: boolean, maxLength?: number}}}
 */
export function useTextInput(options) {
    const [state, setState] = useState({
        value: options?.defaultValue ?? '',
        error: false,
        helperText: '',
        disabled: options?.disabled ?? false,
    });

    const onChange = (e) => {
        let newValue = e.target.value;

        if (options?.noSpaces) {
            newValue = e.target.value.replace(/ /g, '');
        }

        if (options?.format === 'alphanumeric') {
            newValue = e.target.value.replace(/[^0-9a-zA-Z]/g, '');
        }
        if (options?.format === 'numeric') {
            newValue = e.target.value.replace(/[^0-9]/g, '');
        }
        if (options?.format === 'urlSafe') {
            newValue = e.target.value.replace(/[^0-9a-zA-Z-_.~]/g, '');
        }

        if (options?.maxLength) {
            newValue = newValue.slice(0, options.maxLength);
        }

        setState((s) => ({ ...s, value: newValue, error: false, helperText: '' }));
    };

    const setValue = (value) => {
        setState((s) => ({ ...s, value, error: false, helperText: '' }));
    };

    const setError = (message) => {
        setState((s) => ({ ...s, error: true, helperText: message }));
    };

    const setDisabled = (disabled) => {
        disabled === true
            ? setState((s) => ({ ...s, value: '', disabled, helperText: '', error: false }))
            : setState((s) => ({ ...s, disabled }));
    };

    const formProps = {
        value: state.value,
        onChange: onChange,
        error: state.error,
        helperText: state.helperText,
        disabled: state.disabled,
    }

    if (options?.maxLength) {
        formProps.maxLength = options.maxLength;
    }

    return {
        value: state.value,
        setValue: setValue,
        setError: setError,
        setDisabled: setDisabled,
        formProps: formProps,
    };
}

/**
 * Manage number state for TextInput | TextArea components
 * @param {Object} options
 * @param {string|number} [options.defaultValue]
 * @param {boolean} [options.disabled=false]
 * @param {boolean} [options.disablePaste]
 * @param {boolean} [options.integerOnly]
 * @param {'currency'} [options.format]
 * @param {number} [options.decimalPlaces]
 * @param {number} [options.maxLength]
 * @returns {{value: string, setValue: (value: string|number) => void, setError: (message: string) => void, setDisabled: (disabled: boolean) => void, formProps: {value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, error: boolean, helperText: string, disabled: boolean}}}
 */
export function useNumberInput(options) {
    const [state, setState] = useState({
        value:
            typeof options?.defaultValue === 'number' || typeof options?.defaultValue === 'string'
                ? String(options.defaultValue)
                : '',
        error: false,
        helperText: '',
        disabled: options?.disabled ?? false,
    });

    const onChange = (e) => {
        let newValue = e.target.value;

        if (options?.disablePaste) {
            const charDiff = newValue.length - state.value.length;

            if (charDiff > 1 || charDiff === 0) {
                // Pasting is disabled. Ignore when value changes by more then 1 character at a time
                // If the string length didn't change that could mean they replaced the whole string with a string of the same length
                // Edge case: I highlight a portion of the text, and paste in multiple characters but the total character length difference is 1 or negative
                return;
            }
        }

        if (options?.integerOnly) {
            // Integers only
            newValue = newValue.replace(/[^0-9]/g, '');
        } else {
            // Decimal places are allowed
            const numArray = newValue.replace(/[^0-9.]/g, '').split('.');
            newValue = numArray.length > 1 ? numArray[0] + '.' + numArray[1] : numArray[0];

            // Max # of decimal places defined
            if (typeof options?.decimalPlaces === 'number') {
                if (numArray[1] && numArray[1].length > options.decimalPlaces) {
                    newValue = numArray[0] + '.' + numArray[1].slice(0, options.decimalPlaces);
                }
            }
        }

        if (options?.format === 'currency') {
            newValue = formatCurrencyInput(newValue); // Decimal formatting above is also handled in this function (duplicate code)
        }

        if (options?.maxLength) {
            newValue = newValue.slice(0, options.maxLength);
        }

        setState((s) => ({ ...s, value: newValue, error: false, helperText: '' }));
    };

    const setValue = (value) => {
        setState((s) => ({ ...s, value: String(value), error: false, helperText: '' }));
    };

    const setError = (message) => {
        setState((s) => ({ ...s, error: true, helperText: message }));
    };

    const setDisabled = (disabled) => {
        disabled === true
            ? setState((s) => ({ ...s, value: '', disabled, helperText: '', error: false }))
            : setState((s) => ({ ...s, disabled }));
    };

    return {
        value: state.value,
        setValue: setValue,
        setError: setError,
        setDisabled: setDisabled,
        formProps: {
            value: state.value,
            onChange: onChange,
            error: state.error,
            helperText: state.helperText,
            disabled: state.disabled,
        },
    };
}

/**
 * Manage date state for DatePicker | TimePicker components
 * @param {Object} options
 * @param {string|Date|moment.Moment} [options.defaultValue]
 * @param {boolean} [options.disabled=false]
 * @returns {{value: moment.Moment|null, setValue: (value: string|Date|moment.Moment|null) => void, setError: (message: string) => void, formProps: {value: moment.Moment|null, onChange: (value: string|Date|moment.Moment) => void, onBlur: () => void, error: boolean, helperText: string, disabled: boolean}}}
 */
export function useDatePicker(options) {
    const [state, setState] = useState({
        value: options?.defaultValue ? moment(options.defaultValue) : null,
        error: false,
        helperText: '',
        disabled: options?.disabled ?? false,
    });

    const onChange = (value) => {
        let error = Boolean(state.error);
        let helperText = String(state.helperText);

        if (error === true && moment(value).isValid()) {
            // Clear error message if user enters a valid date
            error = false;
            helperText = '';
        }

        // Update value and clear error message on user input
        setState({
            ...state,
            value: moment(value),
            error: error,
            helperText: helperText,
        });
    };

    const setValue = (value) => {
        setState({ ...state, value: moment(value), error: false, helperText: '' });
    };

    const setError = (message) => {
        setState({ ...state, error: true, helperText: message || '' });
    };

    const onBlur = () => {
        // Validate input when user clicks away from the input
        if (state.value !== null && state.value.isValid() === false) {
            setState({ ...state, error: true, helperText: 'Invalid date format' });
        }
    };

    return {
        value: state.value,
        setValue: setValue,
        setError: setError,
        formProps: {
            value: state.value,
            onChange: onChange,
            onBlur: onBlur,
            error: state.error,
            helperText: state.helperText,
            disabled: state.disabled,
        },
    };
}

/**
 * Manage toggle state for Checkbox | Switch components
 * @param {Object} options
 * @param {boolean} [options.defaultValue=false]
 * @param {boolean} [options.disabled=false]
 * @returns {{checked: boolean, setValue: (checked: boolean) => void, setDisabled: (disabled: boolean) => void, formProps: {checked: boolean, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, disabled: boolean}}}
 */
export function useToggle(options) {
    const [state, setState] = useState({
        checked: options?.defaultValue ?? false,
        disabled: options?.disabled ?? false,
    });

    const onChange = (e) => {
        setState((s) => ({ ...s, checked: e.target.checked }));
    };

    const setValue = (checked) => {
        setState((s) => ({ ...s, checked }));
    };

    const setDisabled = (disabled) => {
        setState((s) => ({ ...s, disabled }));
    };

    return {
        checked: state.checked,
        setValue: setValue,
        setDisabled: setDisabled,
        formProps: {
            checked: state.checked,
            onChange: onChange,
            disabled: state.disabled,
        },
    };
}

/**
 * Manage radio state for Radio components
 * @param {Object} options
 * @param {string} [options.defaultValue='']
 * @param {boolean} [options.disabled=false]
 * @returns {{value: string, setValue: (value: string) => void, setError: (message: string) => void, setDisabled: (disabled: boolean) => void, formProps: {value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, disabled: boolean, error: boolean, helperText: string}}}
 */
export function useRadio(options) {
    const [state, setState] = useState({
        value: options?.defaultValue ?? '',
        disabled: options?.disabled ?? false,
        error: false,
        helperText: '',
    });

    const onChange = (e) => {
        setState((s) => ({ ...s, value: e.target.value, error: false, helperText: '' }));
    };

    const setValue = (value) => {
        setState((s) => ({ ...s, value, error: false, helperText: '' }));
    };

    const setDisabled = (disabled) => {
        setState((s) => ({ ...s, disabled }));
    };

    const setError = (message) => {
        setState((s) => ({ ...s, error: true, helperText: message }));
    };

    return {
        value: state.value,
        setValue: setValue,
        setError: setError,
        setDisabled: setDisabled,
        formProps: {
            value: state.value,
            onChange: onChange,
            disabled: state.disabled,
            error: state.error,
            helperText: state.helperText,
        },
    };
}

/**
 * Manage postal code state for TextInput | TextArea components
 * @param {Object} options
 * @param {string} [options.defaultValue='']
 * @param {boolean} [options.disabled=false]
 * @returns {{value: string, setValue: (value: string) => void, setError: (message: string) => void, setDisabled: (disabled: boolean) => void, formProps: {value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, error: boolean, helperText: string, disabled: boolean, maxLength: number}}}
 */
export function usePostalCodeInput(options) {
    const [state, setState] = useState({
        value: options?.defaultValue ?? '',
        error: false,
        helperText: '',
        disabled: options?.disabled ?? false,
    });

    const formatPostalCode = (value) => {
        // Remove all non-alphanumeric characters and convert to uppercase
        const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

        if (cleaned.length <= 3) {
            return cleaned;
        }
        return cleaned.slice(0, 3) + ' ' + cleaned.slice(3, 6);
    };

    const onChange = (e) => {
        const formatted = formatPostalCode(e.target.value);
        setState((s) => ({ ...s, value: formatted, error: false, helperText: '' }));
    };

    const setValue = (value) => {
        const formatted = value ? formatPostalCode(value) : '';
        setState((s) => ({ ...s, value: formatted, error: false, helperText: '' }));
    };

    const setError = (message) => {
        setState((s) => ({ ...s, error: true, helperText: message }));
    };

    const setDisabled = (disabled) => {
        disabled === true
            ? setState((s) => ({ ...s, value: '', disabled, helperText: '', error: false }))
            : setState((s) => ({ ...s, disabled }));
    };

    return {
        value: state.value,
        setValue: setValue,
        setError: setError,
        setDisabled: setDisabled,
        formProps: {
            value: state.value,
            onChange: onChange,
            error: state.error,
            helperText: state.helperText,
            disabled: state.disabled,
            maxLength: 7, // A1A 1A1 = 7 characters
        },
    };
}
